(function (scope, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (scope?.document) api.mount(scope);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  const ROOT = '[data-fas-garden]';
  const DEFAULT_SCENE = Object.freeze({
    width: 1536, height: 1024,
    one: Object.freeze({ x: 931, base: 796, height: 226 }),
    two: Object.freeze({ x: 1298, base: 802, height: 226 }),
    amp: Object.freeze({ x: 1115, base: 800 }), focusX: 1115
  });
  // Match the 900 × 800 canvas in Liquid. Its transparent width accommodates
  // the real M and wide W without shrinking their visible physical height.
  const VISIBLE_HEIGHT = 623 / 800, BASELINE = 651 / 800, CANVAS_RATIO = 900 / 800;
  const CARD_RATIO = 1500 / 945, AMP_RATIO = 1615 / 1829;
  const clamp = (value, lower, upper) => Math.max(lower, Math.min(upper, value));
  const positive = (value, fallback) => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : fallback;

  function placeNote(items, world, viewport, enabled) {
    const note=items.note, support=items.two.visible?items.two:items.one;
    note.paperWidth=note.paperWidth||note.width;
    const smallPack=viewport.smallPair, smallLast=smallPack&&viewport.lastScene;
    note.width=note.paperWidth*(smallLast ? .88 : smallPack ? .82 : 1.08);
    note.height=note.width/CARD_RATIO;
    const pair=items.two.visible,a=smallLast ? .93 : .985,skew=smallLast ? .06 : .10,tilt=smallLast ? .97 : .90;
    const slope=pair?clamp((items.two.base-items.one.base)/(items.two.center-items.one.center),-.08,.08):0;
    const b=slope*a;
    const supportWidth=support.visibleHeight*(support.paintedRatio||.85);
    if(smallLast){
      const minimumLeft=support.center+support.visibleHeight*.07;
      const available=world.width/world.scale-support.visibleHeight*.05-minimumLeft;
      note.width=Math.min(note.width,Math.max(1,available)/(a+skew/CARD_RATIO));
      note.height=note.width/CARD_RATIO;
    }
    const rightLimit=world.width/world.scale-support.visibleHeight*.05-a*note.width;
    note.left=Math.min(support.center+supportWidth*(smallLast ? .24 : smallPack ? .34 : pair ? .12 : .24),rightLimit);
    const overlapHeight=support.visibleHeight*(pair?.30:.52);
    let topY=support.base-Math.min(overlapHeight,note.height*tilt*.88);
    let originY=topY+tilt*note.height-Math.min(0,b*note.width);
    if(smallPack){
      note.tableBase=support.base+slope*(note.left-support.center);
      originY=note.tableBase+support.visibleHeight*.018;
      topY=originY-tilt*note.height+Math.min(0,b*note.width);
    }
    note.top=originY-note.height;
    note.base=originY+Math.max(0,b*note.width);
    note.center=note.left+note.width/2;
    note.visible=enabled===true||enabled==='true';note.plane={a,b,skew,tilt};
    note.support={x:note.left-skew*note.height,y:topY,base:support.base};
    note.foot={x:note.left,y:originY,slope};
    note.projected={left:note.left-skew*note.height,top:topY,width:a*note.width+skew*note.height,height:note.base-topY};
    const castX=smallPack?-.12:viewport.portrait?-.24:-.34,castY=smallPack?-.07:-.16,det=a*tilt-b*skew;
    note.shadowPlane={a:1,b:0,skew:(tilt*castX-skew*castY)/det,tilt:(a*castY-b*castX)/det};
  }

  function sceneGeometry(value) {
    if (!value || typeof value !== 'object') return DEFAULT_SCENE;
    const width = positive(value.width, 1536), height = positive(value.height, 1024);
    const validPoint = point => point && Number.isFinite(point.x) && Number.isFinite(point.base) && point.x >= 0 && point.x <= width && point.base >= 0 && point.base <= height;
    if (!validPoint(value.one) || !validPoint(value.two) || !validPoint(value.amp) || !(value.one.height > 0) || !(value.two.height > 0) || value.one.x === value.two.x) return DEFAULT_SCENE;
    return {
      width, height,
      one: { x: value.one.x, base: value.one.base, height: positive(value.one.height, 226) },
      two: { x: value.two.x, base: value.two.base, height: positive(value.two.height, 226) },
      amp: { x: value.amp.x, base: value.amp.base, ...(value.amp.height > 0 ? {height:value.amp.height} : {}) },
      singleX: Number.isFinite(value.singleX) ? clamp(value.singleX, 0, width) : value.amp.x,
      focusX: Number.isFinite(value.focusX) ? clamp(value.focusX, 0, width) : value.amp.x
    };
  }

  function readGeometry(text) {
    try {
      const value = JSON.parse(text || 'null');
      if (Array.isArray(value) && value.length) return Array.from({ length: 4 }, (_, index) => {
        const entry = value[index];
        return entry?.desktop || entry?.mobile
          ? { desktop: sceneGeometry(entry.desktop), mobile: sceneGeometry(entry.mobile || entry.desktop) }
          : sceneGeometry(entry);
      });
    } catch (_) { /* A broken merchant setting must not hide the product controls. */ }
    return Array.from({ length: 4 }, () => DEFAULT_SCENE);
  }

  function smallSceneGeometry(value) {
    if (!value || typeof value !== 'object') return null;
    const width = value.width, height = value.height;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
    const validPoint = point => point && Number.isFinite(point.x) && Number.isFinite(point.base) && point.x >= 0 && point.x <= width && point.base >= 0 && point.base <= height;
    const validHeight = point => Number.isFinite(point.height) && point.height > 0 && point.height <= point.base;
    if (!validPoint(value.one) || !validPoint(value.two) || !validPoint(value.amp) || !validHeight(value.one) || !validHeight(value.two) || value.one.x === value.two.x) return null;
    const ampHeight = value.amp.height === undefined ? (value.one.height + value.two.height) / 2 : value.amp.height;
    if (!Number.isFinite(ampHeight) || ampHeight <= 0 || ampHeight > value.amp.base) return null;
    return {
      width, height,
      one: { x: value.one.x, base: value.one.base, height: value.one.height },
      two: { x: value.two.x, base: value.two.base, height: value.two.height },
      amp: { x: value.amp.x, base: value.amp.base, height: ampHeight }
    };
  }

  function readSmallGeometry(text) {
    try {
      const value = JSON.parse(text || 'null');
      if (Array.isArray(value)) return Array.from({ length: 4 }, (_, index) => {
        const entry = value[index];
        return entry?.desktop || entry?.mobile
          ? { desktop: smallSceneGeometry(entry.desktop), mobile: smallSceneGeometry(entry.mobile) }
          : smallSceneGeometry(entry);
      });
    } catch (_) { /* Optional photographic calibration never replaces the safe default geometry. */ }
    return Array.from({ length: 4 }, () => null);
  }

  function singleSceneGeometry(value) {
    if(!value||!Number.isFinite(value.width)||!Number.isFinite(value.height)||value.width<=0||value.height<=0)return null;
    const p=value.one;
    if(!p||![p.x,p.base,p.height].every(Number.isFinite)||p.x<0||p.x>value.width||p.base>value.height||p.height<=0||p.height>p.base)return null;
    return {width:value.width,height:value.height,one:{x:p.x,base:p.base,height:p.height}};
  }
  function readSingleGeometry(text) {
    try {const entries=JSON.parse(text||'null');if(Array.isArray(entries))return Array.from({length:4},(_,i)=>{
      const result={};for(const orientation of ['desktop','mobile']){result[orientation]={};for(const size of ['large','small'])result[orientation][size]=singleSceneGeometry(entries[i]?.[orientation]?.[size]);}return result;
    });}catch(_){}return Array.from({length:4},()=>null);
  }
  function singlePhotoItem(point) {
    const height=point.height/VISIBLE_HEIGHT,width=height*CANVAS_RATIO;
    return {left:point.x-width/2,top:point.base-height*BASELINE,width,height,visibleHeight:point.height,base:point.base,center:point.x,visible:true};
  }

  function smallPhotoItems(scene) {
    const letter = point => {
      const height = point.height / VISIBLE_HEIGHT, width = height * CANVAS_RATIO;
      return { left: point.x - width / 2, top: point.base - height * BASELINE, width, height, visibleHeight: point.height, base: point.base, center: point.x, visible: true };
    };
    const ampWidth = scene.amp.height * AMP_RATIO;
    // The pictured initials are 10 cm tall. A 10 cm wide card therefore has
    // their average height as its width, with the original PNG aspect ratio.
    const noteWidth = (scene.one.height + scene.two.height) / 2, noteHeight = noteWidth / CARD_RATIO;
    const noteCenter = scene.two.x + noteWidth * .22;
    const noteBase = scene.one.base + (scene.two.base - scene.one.base) * (noteCenter - scene.one.x) / (scene.two.x - scene.one.x);
    return {
      one: letter(scene.one), two: letter(scene.two),
      amp: { left: scene.amp.x - ampWidth / 2, top: scene.amp.base - scene.amp.height, width: ampWidth, height: scene.amp.height, base: scene.amp.base, center: scene.amp.x, visible: true },
      note: { left: noteCenter - noteWidth / 2, top: noteBase - noteHeight, width: noteWidth, height: noteHeight, base: noteBase, center: noteCenter, visible: true }
    };
  }

  // All measurements stay in the original photograph's coordinate system.
  // The same world rectangle is then applied to both the photo and its controls.
  function projection(options = {}) {
    const width = positive(options.width, 390), height = positive(options.height, 844);
    const portrait = options.orientation ? options.orientation === 'mobile' : width <= height;
    const orientation = portrait ? 'mobile' : 'desktop';
    const artDirected = !!(options.scene?.desktop || options.scene?.mobile);
    const scene = sceneGeometry(artDirected ? options.scene[orientation] || options.scene.desktop : options.scene), small = String(options.size) === '10';
    const pair = options.pair !== false && options.pair !== 'false';
    const editing = !!options.editing && options.editing !== 'false';
    const largeHeight = (scene.one.height + scene.two.height) / 2;
    const visibleHeight = largeHeight * (small ? 10 / 19 : 1);
    const wideOne = String(options.letter1 || '').toUpperCase() === 'W';
    const wideTwo = String(options.letter2 || '').toUpperCase() === 'W';
    const lineBase = x => scene.one.base + (scene.two.base - scene.one.base) * (x - scene.one.x) / (scene.two.x - scene.one.x);
    const firstX = pair ? scene.amp.x + (scene.one.x - scene.amp.x) * (small ? .68 : 1) - (wideOne ? visibleHeight * .1 : 0) : (scene.singleX ?? scene.amp.x);
    const secondX = scene.amp.x + (scene.two.x - scene.amp.x) * (small ? .68 : 1) + (pair && wideTwo ? visibleHeight * .1 : 0);
    const letter = x => {
      const boxHeight = visibleHeight / VISIBLE_HEIGHT, boxWidth = boxHeight * CANVAS_RATIO;
      return { left: x - boxWidth / 2, top: lineBase(x) - boxHeight * BASELINE, width: boxWidth, height: boxHeight, visibleHeight, base: lineBase(x), center: x };
    };
    const ampHeight = scene.amp.height || largeHeight * 10 / 19, ampWidth = ampHeight * AMP_RATIO;
    const noteWidth = largeHeight * 10 / 19, noteHeight = noteWidth / CARD_RATIO;
    const noteCenter = (pair ? secondX : firstX) + largeHeight * .22;
    const items = {
      one: { ...letter(firstX), visible: true },
      two: { ...letter(secondX), visible: pair },
      amp: { left: scene.amp.x - ampWidth / 2, top: scene.amp.base - ampHeight, width: ampWidth, height: ampHeight, base: scene.amp.base, center: scene.amp.x, visible: pair },
      note: { left: noteCenter - noteWidth / 2, top: lineBase(noteCenter) - noteHeight * .8, width: noteWidth, height: noteHeight, base: lineBase(noteCenter), center: noteCenter, visible: true }
    };
    const smallCandidate = options.smallScene?.desktop || options.smallScene?.mobile ? options.smallScene[orientation] : options.smallScene;
    const validSmall = small && pair ? smallSceneGeometry(smallCandidate) : null;
    const photographedSmall = validSmall?.width === scene.width && validSmall.height === scene.height ? validSmall : null;
    // Keep the small photographed placement when the customer changes a glyph
    // or tone. Only the product pixels change; the anchors must not jump inward.
    if (photographedSmall) Object.assign(items, smallPhotoItems(photographedSmall));
    const singleCandidate=options.singleScene?.[orientation]?.[small?'small':'large'];
    const singlePhoto=!pair?singleSceneGeometry(singleCandidate):null;
    if(singlePhoto&&singlePhoto.width===scene.width&&singlePhoto.height===scene.height){
      items.one=singlePhotoItem(singlePhoto.one);
      items.note.width=singlePhoto.one.height*(small?1:10/19);items.note.height=items.note.width/CARD_RATIO;
    }
    const paintedWidth = (item, glyph, character) => {
      const ratio = glyph?.bounds?.height > 0 ? glyph.bounds.width / glyph.bounds.height : (character === 'W' ? 1.3 : .95);
      return Math.min(item.width * .96, item.visibleHeight * clamp(ratio, .15, 1.4));
    };
    items.one.paintedRatio=paintedWidth(items.one,options.glyph1,options.letter1)/items.one.visibleHeight;
    items.two.paintedRatio=paintedWidth(items.two,options.glyph2,options.letter2)/items.two.visibleHeight;
    if (options.custom && pair) {
      const support = photographedSmall || scene;
      const slope = (support.two.base - support.one.base) / (support.two.x - support.one.x);
      const gap = largeHeight * .025;
      const move = (item, center) => {
        const delta = center - item.center;
        item.left += delta; item.center = center; item.top += slope * delta; item.base += slope * delta;
      };
      // Keep a narrow, real gap around the fixed & even for wide M/W glyphs.
      // Use the opaque flower width, not the transparent canvas rectangle.
      move(items.one, Math.min(items.one.center, items.amp.left - gap - paintedWidth(items.one, options.glyph1, options.letter1) / 2));
      move(items.two, Math.max(items.two.center, items.amp.left + items.amp.width + gap + paintedWidth(items.two, options.glyph2, options.letter2) / 2));
    }
    const spread = pair ? largeHeight * .1 * (Number(wideOne) + Number(wideTwo)) : 0;
    const span = Math.abs(scene.two.x - scene.one.x) + largeHeight * 1.1 + spread;
    const fullFirst = scene.one.x - (pair && wideOne ? largeHeight * .1 : 0);
    const fullSecond = scene.two.x + (pair && wideTwo ? largeHeight * .1 : 0);
    const largeBoxBottom = Math.max(lineBase(fullFirst), lineBase(fullSecond)) + largeHeight / VISIBLE_HEIGHT * (1 - BASELINE);
    const largeNoteBottom = lineBase(fullSecond + largeHeight * .22) + noteHeight * .2;
    const productBottom = Math.max(largeBoxBottom, largeNoteBottom, scene.amp.base);
    const productTop = Math.min(lineBase(fullFirst), lineBase(fullSecond)) - largeHeight / VISIBLE_HEIGHT * BASELINE;
    const ratio = width / height;
    const wide = artDirected ? !portrait : (width >= 900 && ratio >= 1.15) || (width >= 500 && ratio >= 1.4);
    let scale, left, top;
    if (wide) {
      scale = Math.max(width / scene.width, height / scene.height);
      if (editing) {
        scale = Math.min(scale, (width - 450) * .88 / span, Math.max(1, height - 100) / (productBottom - productTop));
        left = (width - 430) / 2 - scene.focusX * scale;
      } else left = (width - scene.width * scale) / 2;
      top = (height - scene.height * scale) / 2;
      if (editing) {
        // Reserve the complete XL envelope for both sizes. An ultrawide editor
        // must move the shared photo/control plane up instead of cutting off feet.
        top = clamp(top, 40 - productTop * scale, height - 60 - productBottom * scale);
      } else {
        // An exceptionally wide screen otherwise crops the shelf and the base
        // of the composition. Move the entire cover plane, never the letters.
        // Reserve the full large-pair envelope so switching sizes stays still.
        top = clamp(Math.min(top, height - 60 - productBottom * scale), height - scene.height * scale, 0);
      }
    } else if (artDirected) {
      scale = Math.max(width / scene.width, height / scene.height);
      left = clamp(width / 2 - scene.focusX * scale, width - scene.width * scale, 0);
      top = (height - scene.height * scale) / 2;
      if (editing) {
        const safeHeight = height * .37;
        const editorScale = Math.min(scale, width * .9 / span, safeHeight / (largeHeight * 1.45));
        scale = Math.max(width / scene.width, editorScale);
        left = clamp(width / 2 - scene.focusX * scale, Math.min(0, width - scene.width * scale), 0);
        top = safeHeight - scene.amp.base * scale;
      }
    } else {
      scale = Math.max(width / scene.width, Math.min(width * .92 / (span + 90), Math.max(1, height - 170) / scene.height));
      if (editing) scale *= .85;
      const anchor = editing ? height * .37 : height - (height < 740 ? 285 : 300);
      // Portrait photographs remain wide; moving the shared coordinate plane
      // preserves alignment instead of stretching or cropping each item alone.
      left = clamp(width / 2 - scene.focusX * scale, Math.min(0, width - scene.width * scale), 0);
      top = anchor - scene.amp.base * scale;
    }
    let compositionFit = 1, compositionShift = 0;
    if (artDirected && (wideOne || wideTwo)) {
      const largeBoxWidth = largeHeight / VISIBLE_HEIGHT * CANVAS_RATIO;
      const largeFirst = pair ? scene.one.x - (wideOne ? largeHeight * .1 : 0) : (scene.singleX ?? scene.amp.x);
      const largeSecond = scene.two.x + (wideTwo ? largeHeight * .1 : 0);
      const noteRight = (pair ? largeSecond : largeFirst) + largeHeight * .22 + noteWidth / 2;
      const reserveLeft = photographedSmall ? Math.min(items.one.left, items.two.left, items.note.left) : Math.min(largeFirst - largeBoxWidth / 2, pair ? largeSecond - largeBoxWidth / 2 : Infinity);
      const reserveRight = photographedSmall ? Math.max(items.one.left + items.one.width, items.two.left + items.two.width, items.note.left + items.note.width) : Math.max(largeFirst + largeBoxWidth / 2, pair ? largeSecond + largeBoxWidth / 2 : -Infinity, noteRight);
      const margin = Math.min(14, width * .035), space = width - margin * 2;
      compositionFit = Math.min(1, space / ((reserveRight - reserveLeft) * scale));
      const support = photographedSmall || scene;
      const fittedLeft = left + (support.amp.x + (reserveLeft - support.amp.x) * compositionFit) * scale;
      const fittedRight = left + (support.amp.x + (reserveRight - support.amp.x) * compositionFit) * scale;
      compositionShift = fittedLeft < margin ? (margin - fittedLeft) / scale : fittedRight > width - margin ? (width - margin - fittedRight) / scale : 0;
      const baseShift = (support.two.base - support.one.base) / (support.two.x - support.one.x) * compositionShift;
      for (const item of Object.values(items)) {
        item.left = support.amp.x + (item.left - support.amp.x) * compositionFit + compositionShift;
        item.center = support.amp.x + (item.center - support.amp.x) * compositionFit + compositionShift;
        item.top = support.amp.base + (item.top - support.amp.base) * compositionFit + baseShift;
        item.base = support.amp.base + (item.base - support.amp.base) * compositionFit + baseShift;
        item.width *= compositionFit; item.height *= compositionFit;
        if (item.visibleHeight) item.visibleHeight *= compositionFit;
      }
    }
    if ((small || !pair) && !editing && !singlePhoto) {
      const before = scale, factor = pair ? 1.32 : (small ? 1.52 : 1.18);
      // Bring a single initial and its surrounding home objects closer together.
      // Photo, contact shadows and interactive controls share this camera move.
      const anchor = pair ? items.amp : items.one;
      const anchorX = left + anchor.center * before;
      const anchorY = top + anchor.base * before;
      const visible = [items.one,items.two,items.amp].filter(item => item.visible);
      const minX = Math.min(...visible.map(item => item.left)), maxX = Math.max(...visible.map(item => item.left + item.width));
      scale = Math.max(before, Math.min(before * factor, (width - 28) / (maxX - minX)));
      left = clamp(anchorX - anchor.center * scale, width - scene.width * scale, 0);
      top = clamp(anchorY - anchor.base * scale, height - scene.height * scale, 0);
    }
    const world = { width: scene.width * scale, height: scene.height * scale, left, top, scale };
    const noteViewport = { width, height, portrait, smallPair:small&&pair, lastScene:Number(options.sceneIndex)===3 };
    placeNote(items, world, noteViewport, options.noteEnabled || options.notePreview, options.message);
    if (artDirected && !editing) {
      // Reserve the actual sticky controls, including wrapped mobile labels.
      // Reframe the complete photo and its product plane together; moving only
      // the card or initials would break their contact with the tabletop.
      const safeBottom = positive(options.compositionBottom, height - (portrait ? 298 : 135));
      world.top = clamp(Math.min(world.top, safeBottom - items.note.base * world.scale), height - world.height, 0);
      for (let pass = 0; pass < 3; pass++) {
        if (world.top + items.note.base * world.scale <= safeBottom) break;
        const remainingTable = scene.height - items.note.base + 9 / world.scale;
        const nextScale = Math.max(world.scale, (height - safeBottom + 9) / Math.max(1, remainingTable));
        world.scale = nextScale;
        world.width = scene.width * nextScale; world.height = scene.height * nextScale;
        const focus = pair ? (items.one.center + items.two.center) / 2 : items.one.center;
        world.left = portrait ? clamp(width / 2 - focus * nextScale, width - world.width, 0) : (width - world.width) / 2;
        world.top = height - world.height;
        placeNote(items, world, noteViewport, options.noteEnabled || options.notePreview, options.message);
      }
    }
    if (options.custom && artDirected && !editing) {
      const visible = [[items.one,options.glyph1,options.letter1],[items.two,options.glyph2,options.letter2]].filter(([item]) => item.visible);
      const minX = Math.min(...visible.map(([item,glyph,char]) => item.center - paintedWidth(item,glyph,char)/2));
      const maxX = Math.max(...visible.map(([item,glyph,char]) => item.center + paintedWidth(item,glyph,char)/2));
      const fit = Math.min(1, (width - 28) / ((maxX-minX)*world.scale));
      const support = photographedSmall || scene, anchor = pair ? items.amp.center : items.one.center;
      const fittedLeft = world.left + (anchor + (minX-anchor)*fit)*world.scale;
      const fittedRight = world.left + (anchor + (maxX-anchor)*fit)*world.scale;
      const shift = fittedLeft<14 ? (14-fittedLeft)/world.scale : fittedRight>width-14 ? (width-14-fittedRight)/world.scale : 0;
      if (fit<1 || shift) {
        const baseShift = (support.two.base-support.one.base)/(support.two.x-support.one.x)*shift;
        for (const item of Object.values(items)) {
          item.left = anchor+(item.left-anchor)*fit+shift; item.center = anchor+(item.center-anchor)*fit+shift;
          item.top = support.amp.base+(item.top-support.amp.base)*fit+baseShift;
          item.base = support.amp.base+(item.base-support.amp.base)*fit+baseShift;
          item.width*=fit; item.height*=fit;
          if(item.visibleHeight)item.visibleHeight*=fit;
          if(item.paperWidth)item.paperWidth*=fit;
        }
        compositionFit*=fit; compositionShift+=shift;
        placeNote(items,world,noteViewport,options.noteEnabled||options.notePreview,options.message);
      }
    }
    if (artDirected && portrait && !options.custom && !editing) {
      const photo = photographedSmall || scene;
      const edgeLeft = Math.min(pair?photo.one.x-photo.one.height*.60:items.one.center-items.one.visibleHeight*.60,items.note.projected.left);
      const edgeRight = pair ? photo.two.x+photo.two.height*.65 : items.one.center+items.one.visibleHeight*.60;
      const maxPhotoScale = (width - 24) / (edgeRight - edgeLeft);
      if (world.scale > maxPhotoScale) {
        // Never crop the flowers baked into an integrated photograph. A warm
        // fade below the controls fills the remaining lower edge when a tall
        // phone cannot contain both the full group and a readable card.
        world.scale = maxPhotoScale;
        world.width = scene.width * world.scale; world.height = scene.height * world.scale;
        world.left = width/2 - (edgeLeft+edgeRight)/2*world.scale;
        placeNote(items,world,noteViewport,options.noteEnabled||options.notePreview,options.message);
        world.top = Math.min(0,positive(options.compositionBottom,height-298)-items.note.base*world.scale);
      }
    }
    if(artDirected&&!editing){
      // Fit the whole physical composition, including the paper's support.
      // A close photographic crop must never trigger an unbounded camera zoom.
      const photo=photographedSmall||scene;
      const edge=item=>({left:item.center-item.visibleHeight*.65,right:item.center+item.visibleHeight*.65,top:item.base-item.visibleHeight*1.03});
      const first=!options.custom&&pair?{left:photo.one.x-photo.one.height*.60,right:photo.one.x+photo.one.height*.65,top:photo.one.base-photo.one.height*1.03}:edge(items.one);
      const second=pair?(!options.custom?{left:photo.two.x-photo.two.height*.60,right:photo.two.x+photo.two.height*.65,top:photo.two.base-photo.two.height*1.03}:edge(items.two)):null;
      const minX=Math.min(first.left,second?.left??Infinity,items.note.projected.left);
      const maxX=Math.max(first.right,second?.right??-Infinity,items.note.projected.left+items.note.projected.width);
      const minY=Math.min(first.top,second?.top??Infinity),maxY=items.note.base;
      const bottom=positive(options.compositionBottom,height-(portrait?298:135));
      const photoLimit=portrait?Infinity:Math.max(width/scene.width,height/scene.height);
      world.scale=Math.min(world.scale,photoLimit,(width-28)/(maxX-minX),Math.max(60,bottom-55)/(maxY-minY));
      world.width=scene.width*world.scale;world.height=scene.height*world.scale;
      world.left=clamp((width-world.width)/2,14-minX*world.scale,width-14-maxX*world.scale);
      world.top=clamp((height-world.height)/2,55-minY*world.scale,bottom-maxY*world.scale);
      const controls=options.sideControls;
      if(!portrait&&controls&&[controls.left,controls.right,controls.top].every(Number.isFinite)){
        for(const item of [items.one,items.two].filter(item=>item.visible)){
          const x1=world.left+(item.center-item.visibleHeight*.55)*world.scale;
          const x2=world.left+(item.center+item.visibleHeight*.55)*world.scale;
          if(x1<controls.right&&x2>controls.left)world.top=Math.min(world.top,controls.top-12-item.base*world.scale);
        }
      }
    }
    if(artDirected&&wide&&editing){
      const visible=[items.one,items.two].filter(item=>item.visible);
      const minX=Math.min(...visible.map(item=>item.center-item.visibleHeight*.65),items.note.projected.left);
      const maxX=Math.max(...visible.map(item=>item.center+item.visibleHeight*.65),items.note.projected.left+items.note.projected.width);
      const minY=Math.min(...visible.map(item=>item.base-item.visibleHeight*1.04)),maxY=items.note.base;
      const space=Math.max(200,width-450);
      world.scale=Math.min(Math.max(width/scene.width,height/scene.height),space/(maxX-minX),(height-80)/(maxY-minY));
      world.width=scene.width*world.scale;world.height=scene.height*world.scale;
      world.left=(width-430)/2-(minX+maxX)/2*world.scale;
      world.top=clamp((height-world.height)/2,40-minY*world.scale,height-40-maxY*world.scale);
    }
    world.bottomGap = Math.max(0,height-world.top-world.height);
    return { world, items, scene, wide, orientation, compositionFit, compositionShift, size: small ? 10 : 19, pair, editing };
  }

  function mount(win) {
    const doc = win.document, instances = new Map();
    const Controller = win.AbortController || AbortController, lifecycle = new Controller();
    let disposed = false;
    function on(target, name, callback, abort = lifecycle, options = {}) {
      target?.addEventListener(name, callback, { ...options, signal: abort.signal });
    }
    function init(scope) {
      if (disposed || !scope) return;
      const roots = scope.matches?.(ROOT) ? [scope] : scope.querySelectorAll?.(ROOT) || [];
      roots.forEach(root => {
        if (instances.has(root)) return;
        const stage = root.querySelector('[data-fg-stage]'), form = root.querySelector('[data-fas-form]');
        if (!stage || !form) return;
        const geometry = readGeometry(root.querySelector('script[data-fg-scene-geometry]')?.textContent);
        const smallGeometry = readSmallGeometry(root.querySelector('script[data-fg-small-scene-geometry]')?.textContent);
        const singleGeometry = readSingleGeometry(root.querySelector('script[data-fg-single-scene-geometry]')?.textContent);
        let glyphs = {};
        try { glyphs = JSON.parse(root.querySelector('script[data-fg-letter-library]')?.textContent || '{}').glyphs || {}; } catch (_) { /* Validated fallback widths remain available. */ }
        const portraitMedia = win.matchMedia ? win.matchMedia('(max-aspect-ratio: 1/1)') : null;
        const nodes = { one: root.querySelector('[data-fg-letter="1"]'), two: root.querySelector('[data-fg-letter="2"]'), amp: root.querySelector('.fg-amp'), note: root.querySelector('[data-fg-note]') };
        const abort = new Controller(), originalStyles = new Map(), wideBefore = new Map();
        const sceneSources = new Map(Array.from(root.querySelectorAll('.fg-room picture source')).map(node => [node, node.getAttribute('sizes')]));
        const plateSafeBefore = root.getAttribute('data-fg-plate-safe');
        let frame = null, stopped = false;
        [root, stage].forEach(node => wideBefore.set(node, node.getAttribute('data-fg-wide')));
        function setStyle(node, property, value, priority = '') {
          if (!node) return;
          if (!originalStyles.has(node)) originalStyles.set(node, new Map());
          const saved = originalStyles.get(node);
          if (!saved.has(property)) saved.set(property, { value: node.style.getPropertyValue(property), priority: node.style.getPropertyPriority(property) });
          if (node.style.getPropertyValue(property) !== value || node.style.getPropertyPriority(property) !== priority) node.style.setProperty(property, value, priority);
        }
        function update() {
          frame = null;
          if (stopped || disposed) return;
          const rect = stage.getBoundingClientRect();
          const index = clamp(Math.round(Number(root.dataset.scene ?? stage.dataset.scene) || 0), 0, 3);
          const field = name => form.querySelector('[name="'+name+'"]:checked')?.value ?? form.querySelector('[name="'+name+'"]')?.value;
          const custom = field('letter1')!=='A' || field('tone1')!=='Rosado' || field('theme1')!=='Sin Temática' || (field('offer')!=='single' && (field('letter2')!=='C' || field('tone2')!=='Neutro' || field('theme2')!=='Sin Temática'));
          const purchasePanel = root.querySelector('.fg-purchase-panel');
          const compactPanel = purchasePanel && win.getComputedStyle(purchasePanel).display !== 'contents';
          const controlTop = compactPanel ? purchasePanel : root.querySelector(portraitMedia?.matches ? '.fg-offer-rail' : '.fg-buy-island');
          const result = projection({ width: rect.width || win.innerWidth, height: rect.height || win.innerHeight, orientation: portraitMedia ? (portraitMedia.matches ? 'mobile' : 'desktop') : undefined, scene: geometry[index], sceneIndex:index, smallScene: smallGeometry[index], singleScene: singleGeometry[index], photoMode: root.dataset.fgPhotoMode, photoSize: root.dataset.fgPhotoSize, size: root.dataset.size, pair: root.dataset.pair, editing: root.dataset.fgEditing,
            compositionBottom: controlTop?.getBoundingClientRect().top - rect.top - 14,
            sideControls: root.querySelector('.fg-offer-rail')?.getBoundingClientRect(),
            custom, glyph1:glyphs[field('tone1')]?.[field('letter1')], glyph2:glyphs[field('tone2')]?.[field('letter2')],
            letter1: form.querySelector('[name="letter1"]')?.value, letter2: form.querySelector('[name="letter2"]')?.value,
            notePreview: true, noteEnabled: form.querySelector('[name="offer"]:checked')?.value === 'pareja-xl' || form.querySelector('[name="noteEnabled"]')?.checked === true, message: form.querySelector('[name="message"]')?.value });
          for (const property of ['width', 'height', 'left', 'top']) setStyle(stage, '--fg-scene-' + property, result.world[property].toFixed(4) + 'px');
          setStyle(stage, '--fg-photo-bottom-gap', result.world.bottomGap.toFixed(4) + 'px');
          // The photo plane can extend past the viewport. Let the browser select
          // enough pixels for that full width and the device pixel ratio.
          const sourceSize = Math.ceil(result.world.width) + 'px';
          sceneSources.forEach((original, node) => { if (node.getAttribute('sizes') !== sourceSize) node.setAttribute('sizes', sourceSize); });
          [root, stage].forEach(node => { if (node.dataset.fgWide !== String(result.wide)) node.dataset.fgWide = String(result.wide); });
          const plateSafe = String(Math.abs(result.compositionFit - 1) < .000001 && Math.abs(result.compositionShift) < .000001);
          if (root.dataset.fgPlateSafe !== plateSafe) root.dataset.fgPlateSafe = plateSafe;
          setStyle(stage, '--fg-photo-aspect', result.scene.width + ' / ' + result.scene.height);
          // Keep the gentle background falloff centred on the visible flowers.
          const flowers = [result.items.one, result.items.two, result.items.amp].filter(item => item.visible);
          const flowerLeft = Math.min(...flowers.map(item => item.center - (item.visibleHeight || item.height) * .66));
          const flowerRight = Math.max(...flowers.map(item => item.center + (item.visibleHeight || item.height) * .66));
          const flowerTop = Math.min(...flowers.map(item => item.base - (item.visibleHeight || item.height) * 1.04));
          const flowerBottom = Math.max(...flowers.map(item => item.base));
          setStyle(stage, '--fg-focus-x', ((flowerLeft + flowerRight) / 2 / result.scene.width * 100).toFixed(4) + '%');
          setStyle(stage, '--fg-focus-y', ((flowerTop + flowerBottom) / 2 / result.scene.height * 100).toFixed(4) + '%');
          setStyle(stage, '--fg-focus-rx', ((flowerRight - flowerLeft) * .9 / result.scene.width * 100).toFixed(4) + '%');
          setStyle(stage, '--fg-focus-ry', ((flowerBottom - flowerTop) * 1.05 / result.scene.height * 100).toFixed(4) + '%');
          for (const [key, node] of Object.entries(nodes)) {
            if (!node) continue;
            const item = result.items[key];
            for (const property of ['left', 'top', 'width', 'height']) {
              const extent = property === 'left' || property === 'width' ? result.scene.width : result.scene.height;
              setStyle(node, property, (item[property] / extent * 100).toFixed(7) + '%', 'important');
            }
            setStyle(node, 'bottom', 'auto', 'important');
            setStyle(node, 'right', 'auto', 'important');
            if (key === 'note') {
              setStyle(node, '--fg-note-plane', `matrix(${item.plane.a},${item.plane.b},${item.plane.skew},${item.plane.tilt},0,0)`);
              setStyle(node, '--fg-note-cast-plane', `matrix(${item.shadowPlane.a},${item.shadowPlane.b},${item.shadowPlane.skew},${item.shadowPlane.tilt},0,0)`);
              setStyle(node, 'display', item.visible ? 'block' : 'none', 'important');
            } else if (key === 'one' || key === 'two') {
              const glyph = form.querySelector(key === 'one' ? '[name="letter1"]' : '[name="letter2"]')?.value;
              const feet = /^[AW]$/i.test(String(glyph || ''));
              setStyle(node, '--fg-contact-paint', feet
                ? 'radial-gradient(ellipse at 24% 50%,#302014a8 0%,#30201460 10%,transparent 29%),radial-gradient(ellipse at 76% 50%,#302014a8 0%,#30201460 10%,transparent 29%)'
                : 'radial-gradient(ellipse,#302014a8 5%,#30201460 36%,transparent 74%)');
            }
          }
        }
        function schedule() {
          if (stopped || disposed || frame !== null) return;
          frame = win.requestAnimationFrame(update);
        }
        const mutation = new win.MutationObserver(schedule);
        mutation.observe(root, { attributes: true, attributeFilter: ['data-scene', 'data-size', 'data-pair', 'data-fg-editing', 'data-fg-photo-mode', 'data-fg-photo-size'] });
        const resize = win.ResizeObserver ? new win.ResizeObserver(schedule) : null;
        resize?.observe(stage);
        const purchasePanel = root.querySelector('.fg-purchase-panel');
        if (purchasePanel) resize?.observe(purchasePanel);
        on(win, 'resize', schedule, abort);
        if (portraitMedia?.addEventListener) on(portraitMedia, 'change', schedule, abort);
        on(form, 'input', schedule, abort);
        on(form, 'change', schedule, abort);
        on(root, 'fas:selection', schedule, abort);
        instances.set(root, {
          stop() {
            stopped = true; abort.abort(); mutation.disconnect(); resize?.disconnect();
            if (frame !== null) win.cancelAnimationFrame(frame);
            frame = null;
            originalStyles.forEach((properties, node) => properties.forEach((original, property) => {
              if (original.value) node.style.setProperty(property, original.value, original.priority);
              else node.style.removeProperty(property);
            }));
            wideBefore.forEach((value, node) => { if (value === null) node.removeAttribute('data-fg-wide'); else node.setAttribute('data-fg-wide', value); });
            if (plateSafeBefore === null) root.removeAttribute('data-fg-plate-safe');
            else root.setAttribute('data-fg-plate-safe', plateSafeBefore);
            sceneSources.forEach((original, node) => { if (original === null) node.removeAttribute('sizes'); else node.setAttribute('sizes', original); });
            sceneSources.clear(); originalStyles.clear(); instances.delete(root);
          }
        });
        update();
      });
    }
    function cleanup(scope) {
      instances.forEach((instance, root) => { if (scope === root || scope?.contains?.(root)) instance.stop(); });
    }
    on(doc, 'shopify:section:load', event => init(event.target));
    on(doc, 'shopify:section:unload', event => cleanup(event.target));
    if (doc.readyState === 'loading') on(doc, 'DOMContentLoaded', () => init(doc), lifecycle, { once: true });
    else init(doc);
    return { dispose() { if (disposed) return; disposed = true; lifecycle.abort(); cleanup(doc); } };
  }
  return { projection, readGeometry, readSmallGeometry, readSingleGeometry, mount };
});
